/** AI Campaign & Smart Queue — management API client.
 *
 * Read-side monitoring for an ai_assisted campaign: contacts + their
 * lifecycle status, the immutable event timeline, and funnel stats.
 * See docs/AI_CAMPAIGNS.md.
 */
import { api } from "@/lib/api";

const base = "/v1/ai-campaigns";

export type AIContactStatus = string;

export interface AICampaignContact {
  id: string;
  campaign: string;
  membership: string;
  status: AIContactStatus;
  first_name: string;
  last_name: string;
  phone: string;
  preferred_language: string;
  detected_language: string;
  phone_line_type: string;
  attempt_count: number;
  last_attempt_at: string | null;
  next_action_at: string | null;
  appointment_session: string | null;
  operator: number | null;
  refusal_reason: string;
  escalation_reason: string;
  waiting_since: string | null;
  queue_score: number | null;
  device_ready_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AICampaignEvent {
  id: string;
  previous_status: string;
  new_status: string;
  source: string;
  reason: string;
  provider_call_id: string;
  session: string | null;
  payload_jsonb: Record<string, unknown>;
  occurred_at: string;
}

export interface AICampaignStats {
  campaign_id: string;
  total_contacts: number;
  by_status: Record<string, number>;
  outbound_capacity: number;
  available_operators: number;
}

interface Paginated<T> {
  results: T[];
}

export interface TestCallResult {
  status: string;
  provider_call_id: string;
  phone: string;
}

/** Dial a one-off test call for this campaign's agent, to `phone`. */
export async function placeAITestCall(
  campaignId: string,
  body: { phone: string; first_name?: string },
): Promise<TestCallResult> {
  const res = await api.post<TestCallResult>(
    `${base}/${campaignId}/test-call`,
    body,
  );
  return res.data;
}

export async function enrollAICampaign(campaignId: string): Promise<{ enrolled: number }> {
  const res = await api.post<{ enrolled: number }>(`${base}/${campaignId}/enroll`);
  return res.data;
}

export async function listAIContacts(
  campaignId: string,
  status?: string,
): Promise<AICampaignContact[]> {
  const res = await api.get<Paginated<AICampaignContact>>(
    `${base}/${campaignId}/contacts${status ? `?status=${encodeURIComponent(status)}` : ""}`,
  );
  return res.data.results ?? [];
}

export async function getAIContactTimeline(contactId: string): Promise<AICampaignEvent[]> {
  const res = await api.get<Paginated<AICampaignEvent>>(
    `${base}/contacts/${contactId}/timeline`,
  );
  return res.data.results ?? [];
}

export async function getAICampaignStats(campaignId: string): Promise<AICampaignStats> {
  const res = await api.get<AICampaignStats>(`${base}/${campaignId}/stats`);
  return res.data;
}

export interface AIVoiceCall {
  id: string;
  attempt_number: number;
  provider_call_id: string;
  status: string;
  duration_seconds: number | null;
  has_recording: boolean;
  transcript: unknown;
  started_at: string | null;
  ended_at: string | null;
  created_at: string;
}

export async function getAIContactDetail(
  contactId: string,
): Promise<{ contact: AICampaignContact; voice_calls: AIVoiceCall[] }> {
  const res = await api.get<{ contact: AICampaignContact; voice_calls: AIVoiceCall[] }>(
    `${base}/contacts/${contactId}`,
  );
  return res.data;
}

/** Fetch the signed URL for a call's recording (self-authorising; used as an
 * <audio src>). */
export async function getCallRecordingUrl(callId: string): Promise<string> {
  const res = await api.get<{ url: string }>(`${base}/calls/${callId}/recording`);
  return res.data.url;
}

// --- Agent settings (AI console only) — per-campaign voice-agent prompt +
// the variables it can reference. See docs/ai-agent/CAMPAIGN_CONFIGURABLE_AGENT.md.

export interface AgentVariable {
  key: string;
  label: string;
  applies_to: string;
}

export interface AgentConfig {
  language: string;
  intro: string;
  goal: string;
  // A good call has two parallel success branches: available now vs. wants a
  // scheduled time. Each is configured independently.
  success_now_action: string;
  success_scheduled_action: string;
  fallback_action: string;
}

export interface AgentSettings {
  campaign_id: string;
  campaign_kind: string;
  ai_agent_config: AgentConfig;
  ai_agent_prompt: string;
  advanced_active: boolean;
  compiled_preview: string;
  default_prompt: string;
  options: {
    success_now_actions: string[];
    success_scheduled_actions: string[];
    fallback_actions: string[];
    languages: string[];
  };
  available_variables: {
    core: string[];
    campaign: AgentVariable[];
    runtime: string[];
  };
}

export async function getAgentSettings(campaignId: string): Promise<AgentSettings> {
  const res = await api.get<AgentSettings>(`${base}/${campaignId}/agent-settings`);
  return res.data;
}

export interface AgentSettingsPatch {
  ai_agent_config?: AgentConfig;
  ai_agent_prompt?: string;
}

export async function updateAgentSettings(
  campaignId: string,
  patch: AgentSettingsPatch,
): Promise<{ ai_agent_config: AgentConfig; ai_agent_prompt: string; compiled_preview: string }> {
  const res = await api.patch<{
    ai_agent_config: AgentConfig;
    ai_agent_prompt: string;
    compiled_preview: string;
  }>(`${base}/${campaignId}/agent-settings`, patch);
  return res.data;
}
